/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {useCallback, useEffect, useState} from "react";
import {Box} from "@mui/material";
import {usePostRequest} from "../../../../../api/helper";
import useGetHubName from "../../../../components/Socket/useGetHubName";
import useSocket from "../../../../components/Socket/useSocket";
import {ArgosLoadingButton} from "../../../../components/Buttons/ArgosButton";
import CollapsibleRenderReport from "./CollapsibleRenderReport/CollapsibleRenderReport";
import ArgosFlexbox from "../../../../components/Containers/ArgosFlexbox";
import {toast} from "react-hot-toast";
import {replaceIdsWithNames} from "./helpers";
import {stringToDateToLocaleString} from "../../../../../helpers/helpers";


interface IProps {
  object?: any;
  style: any
}

const ItemsReferencing = (props: IProps) => {
  const [isRunningReport, setIsRunningReport] = useState<any>(false);
  const [isLoadingReport, setIsLoadingReport] = useState<any>(false);
  const [report, setReport] = useState<any>(null);

  useEffect(() => {
    const isRunningReport = report && report['status'] === 'IN PROGRESS';
    setIsRunningReport(isRunningReport)
  }, [report]);

  const reqRefsHandle = usePostRequest('/orchestrators/DurableFunctionsGetRefsToRwoOrch')
  /**
   * Generate report to get refs to the object
   */
  const getRefs = () => {
    if (!props.object?.id) return;
    const payload = {objId: props.object.id};
    setIsRunningReport(true)
    reqRefsHandle(payload).then((res) => {
    }).catch(err => {
      console.error(err);
    })
  }

  const getReportHandle = usePostRequest('/HttpTriggerRwo?type=getReport')
  /**
   * Get report for the object
   * @param onlyStatus - if true, only get the status of the report
   * @param reloadMaps - if true, reload the maps
   */
  const getReport = (onlyStatus: boolean, reloadMaps: boolean) => {
    const payload = {rwoId: props.object.id, onlyStatus, reloadMaps};
    setIsLoadingReport(true)
    getReportHandle(payload).then((res) => {
      if (res) {
        setReport(res);
      }
    }).catch(err => {
      console.error(err);
      toast.error('Failed to load report')
    }).finally(() => {
    }).finally(() => {
      setIsLoadingReport(false)
    })
  }
  useEffect(() => {
    if (props.object?.id) {
      getReport(true, false);
    }
  }, [props.object]);

  const onMessage = useCallback((msg: any) => {
    if (msg.rwo_id !== props.object.id) return;
    if (msg.status === 'SUCCESS') {
      getReport(false, false)
      setIsRunningReport(false);
    } else if (msg.status === 'FAILED') {
      toast.error('Failed to generate report')
      setIsRunningReport(false);
    } else if (msg.status === 'IN PROGRESS') {
      setIsRunningReport(true);
    }
  }, [props.object])
  const hubName = useGetHubName(`rwoRefs`)
  useSocket(hubName, onMessage)

  const reportData = React.useMemo(() => {
    if (!report || !report.data) return null;

    const {
      variant_id_to_referenced_apps,
      variant_id_to_name,
      redline_dataset_id_to_name,
      adas_program_report_id_to_name,
      adas_program_id_to_name_map,
      coverage_analysis_study_id_to_name_map,
      greenline_study_id_to_name_map,
      greenline_id_to_name
    } = report.data;
    if (!variant_id_to_referenced_apps || !variant_id_to_name || !redline_dataset_id_to_name || !adas_program_report_id_to_name || !adas_program_id_to_name_map || !coverage_analysis_study_id_to_name_map || !greenline_study_id_to_name_map || !greenline_id_to_name) return null;

    return replaceIdsWithNames(variant_id_to_referenced_apps, variant_id_to_name, redline_dataset_id_to_name, adas_program_report_id_to_name, adas_program_id_to_name_map, coverage_analysis_study_id_to_name_map, greenline_study_id_to_name_map, greenline_id_to_name);
  }, [report]);

  return (
    <Box p={2}>
      <ArgosFlexbox alignItems={'center'} style={{marginBottom: 20}}>
        {/*Load report*/}
        <ArgosLoadingButton onClick={() => getReport(false, true)} isLoading={isLoadingReport} disabled={isRunningReport}>
          load report
        </ArgosLoadingButton>

        {/*Generate report*/}
        <ArgosLoadingButton onClick={() => getRefs()} isLoading={isRunningReport} disabled={isLoadingReport}>
          generate report
        </ArgosLoadingButton>

        {/*Date generated*/}
        <span>
          {report?.dateGenerated && `Date generated: ${stringToDateToLocaleString(report.dateGenerated)}`}
        </span>
      </ArgosFlexbox>
      <CollapsibleRenderReport data={reportData}/>
    </Box>
  );
};

export default ItemsReferencing;


